import { type ExplorationSpec } from '@/designs-dev/explore/types';

import {
  A1Ledger,
  A2Today,
} from '@/karehero-app/explore/benefits-calculator/QuestionMap';
import {
  B1Asked,
  B2Tapped,
  B3Pruned,
  B4Results,
} from '@/karehero-app/explore/benefits-calculator/Tap';
import {
  C1Proposed,
  C2Confirmed,
  C3Changed,
  C4Batch,
} from '@/karehero-app/explore/benefits-calculator/Proposal';
import {
  D1Invite,
  D2Recording,
  D3Meanwhile,
  D4Landed,
  D5Proposals,
  D6Gaps,
  D7Moved,
} from '@/karehero-app/explore/benefits-calculator/Waffle';
import {
  E1Ask,
  E2Reading,
  E3Found,
  E4Unreadable,
  E5WrongDoc,
} from '@/karehero-app/explore/benefits-calculator/Documents';
import {
  F1Band,
  F2Unsure,
  F3Worth,
  F4Escape,
} from '@/karehero-app/explore/benefits-calculator/Precise';
import {
  G1Conflict,
  G2Subject,
  G3Stale,
  G4Retired,
} from '@/karehero-app/explore/benefits-calculator/Reconcile';
import {
  H1Block,
  H2CarryOn,
  H3Notify,
} from '@/karehero-app/explore/benefits-calculator/Seam';

// One section per way of answering, each shown as every state it passes through
// rather than one representative screen. The states are the point: a mode is
// only as good as what it does when it has nothing to say yet, gets it wrong, or
// comes back late.
//
// The frames run the real ported eligibility engine (front-end/designs/src/
// benefits-flow), which keeps production question ids and band options, so any
// claim about an answer moving the outcome is computed rather than drawn.
export const benefitsCalculatorSpec: ExplorationSpec = {
  id: 'benefits-calculator',
  sourceFile:
    'front-end/designs/src/karehero-app/explore/benefits-calculator/benefits-calculator.tsx',
  title: 'Benefits calculator, every way of answering it',
  by: 'Vez',
  occasion: 'For deciding what to pilot, while the engine is being built',
  stance:
    'The calculator is the test rig, not the prize. Bands instead of figures, nothing submitted anywhere, and a short set almost everyone answers the same way — which makes it the safest place to find out whether a captured answer can be trusted. Cross-form reuse is not testable here at all: at the entry point nothing is held yet, so that belongs to Attendance Allowance.',
  readingNotes: [
    'Every count and every pound is computed by running the real ported engine, so no frame can claim something the engine disagrees with.',
    'This rig proves whether capture can be trusted, not whether it pays. Nothing here is submitted, so being wrong costs a wrong "might be worth pursuing" and nothing else.',
    'The money sits behind three questions a conversation must never answer, and all three are printed on a document someone already has. That is the higher-value experiment available in this rig.',
    'Real-time is out of scope, so capture is static and read later. Nothing arrives as fact — every mode ends in a proposal the member confirms, with the clause it came from.',
  ],
  size: 'large',
  sections: [
    {
      id: 'A',
      title: 'The questions, grouped and ordered',
      intent:
        'The current question set is the source of truth for what has to be answered. Ordered so the route can open with what people volunteer and rated for how far a brain-dump can be trusted with each one.',
      mode: 'probes',
      fidelity: 'deep',
      frames: [
        {
          id: 'A1',
          name: 'How it is grouped today',
          bare: true,
          wide: true,
          inTheFrame:
            'The subcategories and questions as the category seed actually defines them, in the order they are asked, with the one question marked required.',
          chasing:
            'Today the money is asked third and fourth, before anything has been shown to be worth finding a bank statement for, and care needs is asked last. I would argue introducing the user to the platform with care needs is a bit more appropriate and would lead to a more natural conversation.',
          render: () => <A2Today />,
        },
        {
          id: 'A2',
          name: 'Every question, and where its answer comes from',
          bare: true,
          wide: true,
          inTheFrame:
            'All nineteen questions in five groups, in the order the route would ask them. Per question: how far a brain-dump can be trusted with it and why, which channels could answer it, whether the answer goes off, and — computed by dropping it from a fully answered person — what it is load-bearing for.',
          chasing:
            'The reliability ratings are guesses about how much a conversation can be trusted with each question, and every row says why. Go down that column and tell me which ones are wrong.',
          render: () => <A1Ledger />,
        },
      ],
    },
    {
      id: 'R',
      title: 'The route',
      intent:
        'What the order in A1 looks like as a journey. It opens with an open invitation rather than a question, and then keeps going — there is no screen that stops to tell you it is thinking. The note is read in the background while the questions it was never going to answer carry on, and the answers it produced are reviewed later, at a point where reviewing them is the task. Every step here is argued in its own section below; this is the shape.',
      mode: 'sequence',
      fidelity: 'rough',
      frames: [
        {
          id: 'R1',
          name: 'Tell us about your care situation',
          flowOnly: true,
          inTheFrame: 'Its own section, D.',
          render: () => <D1Invite />,
        },
        {
          id: 'R2',
          name: 'Straight on to the next question',
          tap: 'Talk, or type',
          flowOnly: true,
          inTheFrame: 'Its own section, D.',
          render: () => <D3Meanwhile />,
        },
        {
          id: 'R3',
          name: 'The note comes back',
          tap: 'A few minutes later',
          flowOnly: true,
          inTheFrame: 'Its own section, D.',
          render: () => <D4Landed />,
        },
        {
          id: 'R4',
          name: 'Reviewed, now that reviewing is the job',
          tap: 'Check them',
          flowOnly: true,
          inTheFrame: 'Its own section, D.',
          render: () => <D5Proposals />,
        },
        {
          id: 'R5',
          name: 'What it could not settle',
          tap: 'All correct',
          flowOnly: true,
          inTheFrame: 'Its own section, D.',
          render: () => <D6Gaps />,
        },
        {
          id: 'R6',
          name: 'Money last, now it is clearly worth it',
          tap: 'Only the money left',
          flowOnly: true,
          inTheFrame: 'Its own section, F.',
          render: () => <F1Band />,
        },
        {
          id: 'R7',
          name: 'What that was worth',
          tap: 'Answer the three',
          flowOnly: true,
          inTheFrame: 'Its own section, F.',
          render: () => <F3Worth />,
        },
      ],
    },
    {
      id: 'B',
      title: 'Tapping, which is what happens today',
      intent:
        'The control, and in the new route the gap-filler rather than the front door: what is left after the note has been read. Shown at full fidelity because it is good, and because every other section has to earn its place against it.',
      mode: 'sequence',
      fidelity: 'deep',
      frames: [
        {
          id: 'B1',
          name: 'Asked',
          inTheFrame:
            'One question, four options, a progress line. The real screener question and its real options.',
          story: {
            as: 'a carer',
            iWant: 'to be asked one thing at a time',
            soThat: 'I can get through it on a phone in a waiting room',
          },
          render: () => <B1Asked />,
        },
        {
          id: 'B2',
          name: 'Tapped',
          tap: 'Tap "Day and night"',
          inTheFrame:
            'Selected, and about to advance. No Continue on a single-choice question.',
          chasing:
            'Auto-advance is fast and it is also the reason nobody reviews what they just said. Does that matter here, where every answer is a band?',
          render: () => <B2Tapped />,
        },
        {
          id: 'B3',
          name: 'Questions disappearing',
          tap: 'Answer three questions',
          inTheFrame:
            'The early stop, said out loud: what got skipped and why.',
          chasing:
            'Today the pruning is invisible. Should it be? A set that visibly shrinks is the most reassuring thing this flow has going for it, and it currently gets no credit for it.',
          render: () => <B3Pruned />,
        },
        {
          id: 'B4',
          name: 'What comes back',
          tap: 'Finish the six',
          inTheFrame:
            'The eligible list off the real engine, with the more-info-needed count kept as a first-class outcome rather than folded into "no".',
          render: () => <B4Results />,
        },
      ],
    },
    {
      id: 'C',
      title: 'The second visit, when we do hold something',
      intent:
        'Not first contact. If the calculator is the entry point then nothing is pre-filled on the way in, so this section is the return journey: the next benefit, or the same one a season later. Cross-form dedup already works at the data layer — questions share ids across forms, so an answer in one populates another — and the only gap is that nobody ever sees it. That makes this the cheapest mode on the page and the only one with no AI in it. It is also the shape every other mode terminates in, which is why it is argued before the modes that generate proposals.',
      mode: 'sequence',
      fidelity: 'deep',
      frames: [
        {
          id: 'C1',
          name: 'Arrives answered',
          inTheFrame:
            'The question, pre-filled, with where the answer came from on the face of it. Confirm sits above change.',
          story: {
            as: 'a carer',
            iWant: 'not to be asked things I have already told you',
            soThat: 'the second application is shorter than the first',
          },
          chasing:
            'Is "already answered" a badge, or should a held answer look identical to one just given? A badge invites scrutiny, which is what we want, and also invites doubt, which we do not.',
          render: () => <C1Proposed />,
        },
        {
          id: 'C2',
          name: 'Confirmed',
          tap: 'Yes, that is right',
          inTheFrame:
            'Confirmed, and the fact that the same answer sits in her Attendance Allowance form said out loud.',
          chasing:
            'Telling someone their answer went somewhere else is the whole tell-us-once promise. Is that reassuring or is it alarming?',
          render: () => <C2Confirmed />,
        },
        {
          id: 'C3',
          name: 'Changed',
          tap: 'Change it',
          inTheFrame:
            'Straight into the normal question with the old answer selected.',
          chasing:
            'Changing has to be as easy as confirming. If editing is a chore then people confirm things that are wrong, and the confirm step stops being a guardrail.',
          render: () => <C3Changed />,
        },
        {
          id: 'C4',
          name: 'The whole batch at once',
          inTheFrame:
            'Four of the six already known, the two that are not, and one action for the lot.',
          aside: true,
          chasing:
            'Per-question confirm or one batch? Batch is faster and it is also how you get four answers waved through without being read.',
          render: () => <C4Batch />,
        },
      ],
    },
    {
      id: 'D',
      title: 'Waffling at it',
      intent:
        'A brain-dump, read after the fact. This is the section the async constraint bites in, and the sequence is deliberately not question-then-answer: it is submit, carry on with something else, and reconcile when it lands. Read to D7, which is where it stops going the way it was drawn to go.',
      mode: 'sequence',
      fidelity: 'rough',
      frames: [
        {
          id: 'D1',
          name: 'The invitation',
          inTheFrame:
            'An open prompt rather than a question, with typing kept available underneath.',
          story: {
            as: 'a carer',
            iWant: 'to say what is going on in my own words',
            soThat: 'I am not translating my life into multiple choice',
          },
          chasing:
            'Is this offered instead of the questions, or partway through them once we have earned the right to ask? Offering it first risks a two-minute monologue that answers three taps.',
          render: () => <D1Invite />,
        },
        {
          id: 'D2',
          name: 'Recording',
          tap: 'Hold to talk',
          inTheFrame:
            'Listening, with an elapsed count and a clear way to stop.',
          chasing:
            'No live transcription, because nothing is being processed yet. Does silence while recording read as broken?',
          render: () => <D2Recording />,
        },
        {
          id: 'D3',
          name: 'Carrying on while it reads',
          tap: 'Carry on',
          inTheFrame:
            'A quiet strip saying work is happening, and a question the note was never going to answer.',
          chasing:
            'The load-bearing frame of the whole exploration. Early stopping and async capture are complements: the taps are what covers the latency, so a small pruned question set is the reason the seam works rather than the reason it does not.',
          render: () => <D3Meanwhile />,
        },
        {
          id: 'D4',
          name: 'It lands',
          tap: 'A few minutes later',
          inTheFrame:
            'What she actually said, played back, with a count of what was found in it.',
          chasing:
            'Showing the transcript back is honest and it is also long. Does the note itself need to be here, or only the answers taken from it?',
          render: () => <D4Landed />,
        },
        {
          id: 'D5',
          name: 'Five proposals, each with its quote',
          tap: 'Check them',
          inTheFrame:
            'One row per answer, with the clause it came from underneath. Two of these are questions we had not got to yet.',
          chasing:
            'The quote is the anti-fabrication guardrail made visible. Is a quote per answer enough, or does a member need to see the whole note beside them?',
          render: () => <D5Proposals />,
        },
        {
          id: 'D6',
          name: 'What it could not tell us',
          aside: true,
          inTheFrame:
            'The gaps, kept as gaps. She said "the lower one I think", which is not an answer, and she never mentioned money.',
          chasing:
            'A mode that only reports its wins is not checkable. This is the frame that makes the confirm step meaningful — but does anyone read it?',
          render: () => <D6Gaps />,
        },
        {
          id: 'D7',
          name: 'What the note was actually worth',
          tap: 'All correct',
          inTheFrame:
            'The engine run before the note and after it, on the six answers it produced. One benefit appears. The estimated money does not move at all.',
          chasing:
            'This frame was drawn to be the payoff and the engine refused to cooperate, which makes it the most useful frame here. The note answered six real questions about Margaret and moved her list by one non-cash benefit, because the things it could answer were either already covered or gated on something it could not reach. So on this evidence a monologue is not where the value is in the calculator. What follows from that is in section F, and it is not "drop multimodality".',
          render: () => <D7Moved />,
        },
      ],
    },
    {
      id: 'E',
      title: 'Photographing a document',
      intent:
        'Extraction scoped to one question, which was the de-risking suggestion that came out of the 5 Aug session: "get the awards off this letter" is a far easier and safer job than "extract everything relevant". The two failure frames matter more than the success one.',
      mode: 'sequence',
      fidelity: 'rough',
      frames: [
        {
          id: 'E1',
          name: 'Asked for one document',
          inTheFrame:
            'A named document for a named purpose, with answering it yourself always available.',
          story: {
            as: 'a carer',
            iWant: 'to photograph the letter rather than decipher it',
            soThat: 'I do not have to work out which benefit she is on',
          },
          chasing:
            'Naming the document and the reason is what keeps this from feeling like a data grab. Is the ask specific enough that someone can go and find the right envelope?',
          render: () => <E1Ask />,
        },
        {
          id: 'E2',
          name: 'Reading it',
          tap: 'Take a photo',
          inTheFrame:
            'The same seam as the voice note, and the scope stated: we are looking for one thing and not reading the rest.',
          chasing:
            'Saying what we are not reading is a privacy move and a trust move. Is it believed?',
          render: () => <E2Reading />,
        },
        {
          id: 'E3',
          name: 'One field back',
          tap: 'It comes back',
          inTheFrame:
            'The award, with the line off the letter it came from. Not the address, not the date, not the reference number.',
          chasing:
            'Scoped extraction leaves obvious value on the table — her address was right there. Is the restraint worth it, or does it just mean two photos?',
          render: () => <E3Found />,
        },
        {
          id: 'E4',
          name: 'Cannot read it',
          aside: true,
          inTheFrame:
            'No answer taken, said plainly, with a retake and a way to type it instead.',
          chasing:
            'A document mode that cannot say this will quietly invent a figure. A wrong number off a letter is the same class of harm as invented prose, and this frame is the guardrail.',
          render: () => <E4Unreadable />,
        },
        {
          id: 'E5',
          name: 'Wrong document',
          aside: true,
          inTheFrame:
            'Readable, and useless. It says what it was expecting instead.',
          chasing:
            'Telling someone which letter we wanted is more useful than telling them this one failed. How specific can we be without sounding like a form?',
          render: () => <E5WrongDoc />,
        },
      ],
    },
    {
      id: 'F',
      title: 'The three nobody knows, and what they are worth',
      intent:
        'Her savings, her weekly income, and what her carer earns. All three are bands, all three are printed on a document, and none is answerable from memory. F3 is where the exploration lands: measured against the same engine, these three move almost all of the money, and the voice note moved none of it.',
      mode: 'sequence',
      fidelity: 'sketch',
      frames: [
        {
          id: 'F1',
          name: 'A band, not a figure',
          inTheFrame:
            'Three options, and the reason there are only three. Under £16,000, £16,000 to £23,250, over £23,250.',
          chasing:
            'The tolerance here is the whole reason this is a safer place to try extraction than an Attendance Allowance form. Does saying so out loud help, or does explaining the threshold invite gaming it?',
          render: () => <F1Band />,
        },
        {
          id: 'F2',
          name: 'Going to look',
          tap: 'I need to check',
          inTheFrame:
            'Leaving is a legitimate ending. Everything else is held, and this is the only thing outstanding.',
          story: {
            as: 'a carer',
            iWant: 'to go and find her bank statement without losing my place',
            soThat: 'I do not have to start again this evening',
          },
          chasing:
            'What brings them back, and how long is the record good for before it needs reconfirming?',
          render: () => <F2Unsure />,
        },
        {
          id: 'F3',
          name: 'What three numbers are worth',
          inTheFrame:
            'The same before-and-after as D7, on the three typed answers instead of the note. Her savings, her weekly income, and what her daughter earns.',
          chasing:
            'This is where the money is, by a distance, and it is the frame that decides what to build. Every one of these three is written on a document she already has — a bank statement, a pension letter, a payslip. So the case for multimodality here survives, but it is document extraction pointed at three named documents, not a voice note. That is a much smaller and much more testable thing to build than a conversational agent.',
          render: () => <F3Worth />,
        },
        {
          id: 'F4',
          name: 'Out of the queue',
          aside: true,
          inTheFrame:
            'Still reading your note, and here is the question anyway. Answer it yourself and we drop it from the note when it arrives.',
          chasing:
            'No async state is allowed to be a dead end. This is the frame that guarantees it — every queued mode has this exit.',
          render: () => <F4Escape />,
        },
      ],
    },
    {
      id: 'G',
      title: 'When a proposal is wrong',
      intent:
        'The four ways this breaks. None is an edge case: they all follow directly from holding a record over time and letting answers arrive from more than one place.',
      mode: 'probes',
      fidelity: 'rough',
      frames: [
        {
          id: 'G1',
          name: 'Two answers that disagree',
          inTheFrame:
            'March’s answer and today’s note, side by side, with the system declining to pick.',
          chasing:
            'It cannot know which is current, so it has to ask. How often can it ask this before it feels broken?',
          render: () => <G1Conflict />,
        },
        {
          id: 'G2',
          name: 'Whose fact is this',
          inTheFrame:
            'A carer talking about themselves and about Margaret in one breath, and the system asking which one it heard.',
          chasing:
            'The one to worry about most. A fact filed against the wrong person poisons everything downstream, and the engine already models subject as a first-class field — so this is a surface for something that exists, not a new idea.',
          render: () => <G2Subject />,
        },
        {
          id: 'G3',
          name: 'Still true?',
          inTheFrame:
            'Two answers from March, the two most likely to have moved, asked as one question.',
          chasing:
            'Facts already carry an expiry in production. What decides which questions get one, and what cadence stops this becoming nagging?',
          render: () => <G3Stale />,
        },
        {
          id: 'G4',
          name: 'An answer we no longer need',
          inTheFrame:
            'The note answered the six-month question, and she already qualifies on age. Kept on the record, and not used.',
          chasing:
            'Dropped silently, it looks like the note was ignored. Said out loud, it is a screen nobody needed. Which is worse?',
          render: () => <G4Retired />,
        },
      ],
    },
    {
      id: 'H',
      title: 'The seam itself',
      intent:
        'Three answers to the one question the technical plan forces on us: they have submitted something and we are not ready. Real-time is out of scope, so this is not avoidable, only designable. Everything above assumes the middle one.',
      mode: 'rivals',
      fidelity: 'rough',
      frames: [
        {
          id: 'H1',
          name: 'Wait for it',
          inTheFrame:
            'A progress state and nothing else to do until the note has been read.',
          optimises:
            'Simple, honest, and one thing on screen at a time. No reconciliation problem, because nothing has moved on.',
          costs:
            'Makes the latency the member’s problem, on a journey whose whole promise is that it is easier than the form. Two minutes of nothing is where people close the app.',
          chasing:
            'Is there a duration short enough that waiting is fine? If processing were fifteen seconds this would be the right answer.',
          render: () => <H1Block />,
        },
        {
          id: 'H2',
          name: 'Carry on regardless',
          inTheFrame:
            'A quiet strip, and the next question the note was never going to answer.',
          optimises:
            'The latency disappears behind work the member was going to do anyway. It also puts the taps and the note on the same footing, so neither mode is the primary one.',
          costs:
            'Everything in section G. Answers arriving out of order against a set that is still pruning is the hardest state to hold, and the one most likely to produce a screen nobody designed.',
          chasing:
            'This is what the rest of the page assumes. The reason it can work is the minimisation itself: a short pruned set is exactly what makes there be something else to ask.',
          render: () => <H2CarryOn />,
        },
        {
          id: 'H3',
          name: 'Off you go, we will message you',
          inTheFrame:
            'A deliberate ending, and a notification when the answers are ready.',
          optimises:
            'Fits short droppable sessions, which is what the team leaned towards on 5 Aug. Costs nothing while it processes, and eligibility results are already pushed to the client, so the channel exists.',
          costs:
            'Ends the session at the exact moment momentum was highest. Getting them back is a separate problem, and probably a harder one than the latency.',
          chasing:
            'Is this the right ending for a member who has already given us the note, or only for one who has run out of things they can answer?',
          render: () => <H3Notify />,
        },
      ],
    },
  ],
  walkthrough: [
    'A1. How it is asked today: the money third, care needs last. That is the case for regrouping.',
    'A2, the ledger. Go down the reliability column and tell me which rows are wrong. That argument is the whole meeting.',
    'R. The route that falls out of A1: open invitation, gaps, money last.',
    'B and C, quickly. Taps and pre-filled answers are what fill the gaps, and C works at the data layer today with no AI in it.',
    'D3, slowly. Submitting does not stop anything: the next question is just there, with a quiet strip saying the note is being read. If that frame reads as fine, async capture is fine.',
    'D7, then F3, back to back. This is the finding. The note moved one non-cash benefit; three typed numbers moved almost all the money.',
    'E4 and E5. The failure frames are the ones that decide whether documents are safe.',
    'G2. Decide whether subject binding is a launch blocker.',
    'H. Pick one. Everything above assumes H2.',
  ],
  thread:
    'Two different things are being tested and they belong in different places. Whether a captured answer can be trusted belongs here, because nothing is submitted and every answer is a band. Whether reuse pays belongs to Attendance Allowance, because at the entry point there is nothing held to reuse. What this page found on the way is that a monologue is not where the value is: six answers out of a note moved Margaret by one non-cash benefit and no money at all, while three answers off a bank statement, a pension letter and a payslip moved almost all of it. Same pipeline, much narrower prompt, far less room to fabricate.',
};
